import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useEffectEvent, useMemo } from 'react';
import { useIntegrations } from './accountHooks';
import z from 'zod';
import * as R from 'remeda';
import { useRouter } from 'next/navigation';
import { ToastContext } from '@/components/Toasts';
import dashboardMenuItems, { AccessLevel, NAV_ITEMS_BY_ROLE, NavItem } from '@/routes/DashboardRoutes';
import { useGlobalAccountContext } from '@/components/AccountPicker';
import { useSession } from '@/api/auth/client';

/**
 * Hook to check feature availability for an account based on integration configuration
 * @param accountId - The account ID to check features for
 * @returns An object with feature flags and a helper function to check if a feature is enabled
 */
export const Feature = z.enum(['StaffIQ', 'DataIQ', 'Analytics', 'Settings']);
export type Feature = z.infer<typeof Feature>;
interface FeatureAvailability {
  features: Readonly<Record<Feature, boolean>>;
  isLoading: boolean;
}
export function useFeatureAvailability(accountId) {
  const $ = _c(10);
  const {
    integrations,
    isLoading
  } = useIntegrations(accountId);
  let t0;
  if ($[0] !== integrations) {
    t0 = integrations?.find(_temp);
    $[0] = integrations;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const bullhorn = t0;
  const config = bullhorn?.config;
  let t1;
  bb0: {
    if (!config) {
      let t2;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          StaffIQ: false,
          DataIQ: false,
          Analytics: false,
          Settings: false
        };
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t1 = t2;
      break bb0;
    }
    const staffIq = config.staffIqConfig?.enabled === true;
    const t2 = config.dataIqConfig?.enabled === true;
    const t3 = staffIq && config.analyticsConfig?.enabled === true;
    let t4;
    if ($[3] !== staffIq || $[4] !== t2 || $[5] !== t3) {
      t4 = {
        StaffIQ: staffIq,
        DataIQ: t2,
        Analytics: t3,
        Settings: staffIq
      };
      $[3] = staffIq;
      $[4] = t2;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t1 = t4;
  }
  const features = t1;
  let t2;
  if ($[7] !== features || $[8] !== isLoading) {
    t2 = {
      features,
      isLoading
    };
    $[7] = features;
    $[8] = isLoading;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function _temp(i) {
  return i.type === "bullhorn";
}
export function useNavRoutes() {
  const $ = _c(10);
  const {
    data: session
  } = useSession();
  const user = session?.user;
  const {
    selectedAccount,
    isClient
  } = useGlobalAccountContext();
  const accessLevel = !user ? "public" : user.role || "user";
  const {
    features
  } = useFeatureAvailability(isClient ? selectedAccount?._id : undefined);
  const itemsByRole = NAV_ITEMS_BY_ROLE[accessLevel];
  let t0;
  if ($[0] !== features || $[1] !== isClient || $[2] !== itemsByRole) {
    let t1;
    if ($[4] !== features || $[5] !== isClient) {
      t1 = item => {
        if (!isClient) {
          return true;
        }
        const result = Feature.safeParse(item.title);
        return result.success && features[result.data];
      };
      $[4] = features;
      $[5] = isClient;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    t0 = itemsByRole.filter(t1);
    $[0] = features;
    $[1] = isClient;
    $[2] = itemsByRole;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const navItems = t0;
  const t1 = !user;
  let t2;
  if ($[7] !== navItems || $[8] !== t1) {
    t2 = {
      navItems,
      isLoading: t1
    };
    $[7] = navItems;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

/**
 * Hook to check feature access and redirect if user doesn't have access
 * @redirects user to another page depending on what features they have access to
 */
export function useFeatureGuard(requiredFeature, accountId) {
  const $ = _c(17);
  const router = useRouter();
  const {
    isAdmin
  } = useGlobalAccountContext();
  const {
    features,
    isLoading
  } = useFeatureAvailability(accountId);
  const {
    errorToast
  } = useContext(ToastContext);
  const hasAccess = !isAdmin && !isLoading && requiredFeature ? features[requiredFeature] : true;
  const {
    navItems: availableRoutes
  } = useNavRoutes();
  let t0;
  if ($[0] !== availableRoutes || $[1] !== hasAccess) {
    t0 = hasAccess ? null : availableRoutes.find(_temp2);
    $[0] = availableRoutes;
    $[1] = hasAccess;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const routeToRedirectTo = t0;
  let t1;
  if ($[3] !== errorToast || $[4] !== router) {
    t1 = (route_0, requiredFeature_0) => {
      const error = `You don't have access to ${requiredFeature_0 || "this feature"}`;
      if (route_0?.link) {
        errorToast(`${error}. Redirecting to ${route_0.title}.`);
        router.push(route_0.link);
      } else {
        errorToast(error);
        router.push("/no-access");
      }
    };
    $[3] = errorToast;
    $[4] = router;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const redirectToFeature = useEffectEvent(t1);
  let t2;
  if ($[6] !== hasAccess || $[7] !== redirectToFeature || $[8] !== requiredFeature || $[9] !== routeToRedirectTo) {
    t2 = () => {
      if (!hasAccess) {
        redirectToFeature(routeToRedirectTo, requiredFeature);
      }
    };
    $[6] = hasAccess;
    $[7] = redirectToFeature;
    $[8] = requiredFeature;
    $[9] = routeToRedirectTo;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== hasAccess || $[12] !== requiredFeature || $[13] !== routeToRedirectTo) {
    t3 = [hasAccess, requiredFeature, routeToRedirectTo];
    $[11] = hasAccess;
    $[12] = requiredFeature;
    $[13] = routeToRedirectTo;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  useEffect(t2, t3);
  let t4;
  if ($[15] !== isLoading) {
    t4 = {
      isLoading
    };
    $[15] = isLoading;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
function _temp2(route) {
  return R.isString(route.link);
}